import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import axios from "axios";
import JSZip from "jszip";
import { serverLink } from "../../../resources/url";
import { toast } from "react-toastify";
import { showAlert } from "../../common/sweetalert/sweetalert";
import DataLoader from "../../common/dataLoader/dataLoader";
import DataTable from "../../common/data-table/data-table";
import { formatDateAndTime } from "../../../resources/constants";

const PASSPORT_BASE = "https://kadpolyodfel.ng/assets/passport/";
const FALLBACK_IMG = "https://kadpolyodfel.ng/assets/passport/avatar.png";

// ── Passport card used inside the Validate Passport modal ──────────────────────
function PassportCard({ item, serverLink }) {
    const passportUrl = `${serverLink}staff/registration/registration/student/passport-by-appid/${item.AppID}`;

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "12px 10px 10px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "6px",
                minWidth: 0,
                boxShadow: "0 1px 4px rgba(0,0,0,.07)",
            }}
        >
            {/* Photo */}
            <div
                style={{
                    width: "80px",
                    height: "96px",
                    borderRadius: "6px",
                    overflow: "hidden",
                    background: "#f1f5f9",
                    border: "2px solid #e2e8f0",
                    flexShrink: 0,
                }}
            >
                <img
                    src={passportUrl}
                    alt={item.StudentName || item.AppID}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => { e.target.src = FALLBACK_IMG; }}
                    loading="lazy"
                />
            </div>

            {/* Status badge */}
            <span
                className={`badge ${item.Status === "Completed" ? "bg-success" : "bg-warning"}`}
                style={{ fontSize: "0.65rem" }}
            >
                {item.Status}
            </span>

            {/* Name */}
            <p
                style={{
                    margin: 0,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    textAlign: "center",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                    color: "#1e293b",
                }}
                title={item.StudentName}
            >
                {item.StudentName || "—"}
            </p>

            {/* AppID */}
            <p
                style={{
                    margin: 0,
                    fontSize: "0.65rem",
                    color: "#64748b",
                    fontFamily: "monospace",
                    textAlign: "center",
                }}
            >
                {item.AppID}
            </p>

            {/* StudentID if available */}
            {item.StudentID && (
                <p
                    style={{
                        margin: 0,
                        fontSize: "0.62rem",
                        color: "#94a3b8",
                        textAlign: "center",
                    }}
                >
                    {item.StudentID}
                </p>
            )}
        </div>
    );
}
// ───────────────────────────────────────────────────────────────────────────────

function StudentIdCardReport(props) {
    const [IsLoading, setIsLoading] = useState(true);
    const [IsActionLoading, setIsActionLoading] = useState(false);
    const [IsZipping, setIsZipping] = useState(false);
    const [IsModalZipping, setIsModalZipping] = useState(false);
    const [recordList, setRecordList] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [passportSearch, setPassportSearch] = useState("");

    const header = [
        "S/N", "AppID", "StudentID", "Student Name", "Gender",
        "Blood Group", "NIN", "Programme", "Status", "Inserted By", "Date", "Action",
    ];

    useEffect(() => {
        getData();
    }, [""]);

    const getData = async () => {
        await axios
            .get(`${serverLink}staff/registration/registration/student/id-card-processing`)
            .then((result) => {
                setRecordList(result.data);
                setIsLoading(false);
            })
            .catch(() => {
                console.log("NETWORK ERROR");
                setIsLoading(false);
            });
    };

    const markCompleted = async (AppID) => {
        setIsActionLoading(true);
        await axios
            .patch(`${serverLink}staff/registration/registration/student/id-card-processing/complete`, {
                AppID,
                UpdatedBy: props.loginData[0]?.StaffID,
            })
            .then((result) => {
                if (result.data.message === "success") {
                    toast.success("Status updated to Completed successfully");
                    getData();
                } else {
                    showAlert("ERROR", "Something went wrong. Please try again!", "error");
                }
                setIsActionLoading(false);
            })
            .catch(() => {
                setIsActionLoading(false);
                showAlert("NETWORK ERROR", "Please check your connection and try again!", "error");
            });
    };

    const showTable = () => {
        try {
            return recordList.map((item, index) => {
                const isCompleted = item.Status === "Completed";
                const statusClass = isCompleted ? "text-success" : "text-warning";
                return (
                    <tr key={index}>
                        <td className="text-xs font-weight-bold">{index + 1}</td>
                        <td className="text-xs font-weight-bold">{item.AppID}</td>
                        <td className="text-xs font-weight-bold">{item.StudentID}</td>
                        <td className="text-xs font-weight-bold">{item.StudentName}</td>
                        <td className="text-xs font-weight-bold">{item.GENDER || "N/A"}</td>
                        <td className="text-xs font-weight-bold">{item.BLOOD_GROUP || "N/A"}</td>
                        <td className="text-xs font-weight-bold">{item.NIN || "N/A"}</td>
                        <td className="text-xs font-weight-bold">{item.Programme}</td>
                        <td className={`text-xs font-weight-bold ${statusClass}`}>{item.Status}</td>
                        <td className="text-xs font-weight-bold">{item.InsertedBy}</td>
                        <td className="text-xs font-weight-bold">
                            {item.InsertedDate ? formatDateAndTime(item.InsertedDate, "date") : "N/A"}
                        </td>
                        <td className="text-xs font-weight-bold">
                            <div className="d-flex gap-1">
                                <button
                                    className="btn btn-info btn-sm"
                                    data-bs-toggle="modal"
                                    data-bs-target="#modal-student-details"
                                    onClick={() => setSelectedRecord(item)}
                                >
                                    View
                                </button>
                                {isCompleted ? (
                                    <span className="badge bg-success align-self-center">Completed</span>
                                ) : (
                                    <button
                                        className="btn btn-success btn-sm"
                                        disabled={IsActionLoading}
                                        onClick={() => markCompleted(item.AppID)}
                                    >
                                        Complete
                                    </button>
                                )}
                            </div>
                        </td>
                    </tr>
                );
            });
        } catch (e) {
            alert(e.message);
        }
    };

    const downloadAllImages = async () => {
        const withPictures = recordList.filter((item) => item.PICTURE);
        if (withPictures.length === 0) {
            toast.warning("No passport images found to download.");
            return;
        }
        setIsZipping(true);
        const zip = new JSZip();
        let successCount = 0;
        let failCount = 0;

        await Promise.all(
            withPictures.map(async (item) => {
                const proxyUrl = `${serverLink}staff/registration/registration/student/passport-proxy?file=${encodeURIComponent(item.PICTURE)}`;
                const safeId = (item.StudentID || item.AppID || "unknown").replace(/\//g, "_");
                const ext = item.PICTURE.split(".").pop().split("?")[0] || "jpg";
                const filename = `${safeId}.${ext}`;
                try {
                    const response = await fetch(proxyUrl);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const blob = await response.blob();
                    zip.file(filename, blob);
                    successCount++;
                } catch {
                    failCount++;
                }
            })
        );

        if (successCount === 0) {
            toast.error("Failed to fetch any images. Please check your connection.");
            setIsZipping(false);
            return;
        }

        try {
            const content = await zip.generateAsync({ type: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(content);
            link.download = "student-passports.zip";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success(
                `ZIP downloaded! ${successCount} image(s) included.${
                    failCount > 0 ? ` ${failCount} failed.` : ""
                }`
            );
        } catch {
            toast.error("Failed to generate ZIP file.");
        }
        setIsZipping(false);
    };

    const passportSrc = selectedRecord?.PICTURE
        ? `${PASSPORT_BASE}${selectedRecord.PICTURE}`
        : FALLBACK_IMG;

    // Filtered list for the validate-passport modal search box
    const filteredForValidation = passportSearch.trim()
        ? recordList.filter((r) => {
            const q = passportSearch.toLowerCase();
            return (
                (r.StudentName || "").toLowerCase().includes(q) ||
                (r.AppID || "").toString().includes(q) ||
                (r.StudentID || "").toLowerCase().includes(q)
            );
        })
        : recordList;

    // Download all CURRENTLY VISIBLE passport images in the modal as a ZIP
    const downloadModalImages = async () => {
        if (filteredForValidation.length === 0) {
            toast.warning("No students to download.");
            return;
        }
        setIsModalZipping(true);
        const zip = new JSZip();
        let successCount = 0;
        let failCount = 0;

        await Promise.all(
            filteredForValidation.map(async (item) => {
                const proxyUrl = `${serverLink}staff/registration/registration/student/passport-by-appid/${item.AppID}`;
                const safeId = (item.StudentID || item.AppID || "unknown").replace(/\//g, "_");
                try {
                    const response = await fetch(proxyUrl);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const blob = await response.blob();
                    // Detect extension from content-type
                    const ct = blob.type || "image/jpeg";
                    const ext = ct.split("/")[1]?.replace("jpeg", "jpg") || "jpg";
                    zip.file(`${safeId}.${ext}`, blob);
                    successCount++;
                } catch {
                    failCount++;
                }
            })
        );

        if (successCount === 0) {
            toast.error("No images could be downloaded. Please check your connection.");
            setIsModalZipping(false);
            return;
        }

        try {
            const content = await zip.generateAsync({ type: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(content);
            link.download = "student-passports.zip";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success(
                `ZIP downloaded! ${successCount} image(s) included.${
                    failCount > 0 ? ` ${failCount} failed.` : ""
                }`
            );
        } catch {
            toast.error("Failed to generate ZIP file.");
        }
        setIsModalZipping(false);
    };

    return IsLoading ? (
        <div className="container-xl" style={{ height: "700px" }}>
            <div className="page-header d-print-none">
                <DataLoader />
            </div>
        </div>
    ) : (
        <>
            <div className="container-xl">
                <div className="page-header d-print-none">
                    <div className="row align-items-center">
                        <div className="col">
                            <div className="page-pretitle"></div>
                            <h2 className="page-title">Student ID Card Processing Report</h2>
                        </div>
                    </div>
                </div>
            </div>
            <div className="page-body">
                <div className="container-xl">
                    <div className="row row-deck row-cards">
                        <div className="col-lg-12">
                            <div className="row row-cards">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                                <h3 className="card-title mb-0">Student ID Card Processing Report</h3>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {/* ── Validate Passport button ── */}
                                                    <button
                                                        className="btn btn-warning btn-sm"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#modal-validate-passport"
                                                        onClick={() => setPassportSearch("")}
                                                        disabled={recordList.length === 0}
                                                        title="View all student passport photos in a grid"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <circle cx="11" cy="11" r="8" />
                                                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                        </svg>
                                                        Validate Passport
                                                    </button>

                                                    {/* ── Download ZIP button ── */}
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        onClick={downloadAllImages}
                                                        disabled={IsZipping || recordList.filter(r => r.PICTURE).length === 0}
                                                        title="Download all passport photos as a ZIP file"
                                                    >
                                                        {IsZipping ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                                Zipping…
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                                    <polyline points="7 10 12 15 17 10" />
                                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                                </svg>
                                                                Download All Photos (ZIP)
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                            <DataTable
                                                header={header}
                                                body={showTable()}
                                                title="Student ID Card Processing Report"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ────────────── Validate Passport Modal ────────────── */}
            <div
                className="modal modal-blur fade"
                id="modal-validate-passport"
                tabIndex="-1"
                role="dialog"
                aria-hidden="true"
            >
                <div
                    className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
                    role="document"
                >
                    <div className="modal-content">
                        <div className="modal-header" style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                            <div className="d-flex align-items-center gap-3 flex-grow-1">
                                <div>
                                    <h5 className="modal-title mb-0">Validate Passports</h5>
                                    <small className="text-muted">
                                        {recordList.length} student{recordList.length !== 1 ? "s" : ""} — images loaded on scroll
                                    </small>
                                </div>
                                {/* Search box */}
                                <div className="ms-auto" style={{ maxWidth: "260px", width: "100%" }}>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search name, AppID, StudentID…"
                                        value={passportSearch}
                                        onChange={(e) => setPassportSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn-close ms-3"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            />
                        </div>

                        <div className="modal-body" style={{ background: "#f1f5f9", minHeight: "400px" }}>
                            {filteredForValidation.length === 0 ? (
                                <div className="text-center text-muted py-5">No students match your search.</div>
                            ) : (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                                        gap: "12px",
                                        padding: "4px 0",
                                    }}
                                >
                                    {filteredForValidation.map((item, index) => (
                                        <PassportCard
                                            key={item.AppID || index}
                                            item={item}
                                            serverLink={serverLink}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ background: "#f8fafc" }}>
                            <span className="text-muted small me-auto">
                                Showing {filteredForValidation.length} of {recordList.length} students
                            </span>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={downloadModalImages}
                                disabled={IsModalZipping || filteredForValidation.length === 0}
                                title="Download all visible passport photos as a ZIP"
                            >
                                {IsModalZipping ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        Zipping…
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                        ZIP &amp; Download
                                    </>
                                )}
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" data-bs-dismiss="modal">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ────────────── Student Details Modal ────────────── */}
            <div className="modal modal-blur fade" id="modal-student-details" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Student ID Card Details</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body">
                            {selectedRecord && (
                                <div className="row">
                                    {/* Passport Photo */}
                                    <div className="col-md-3 text-center mb-3">
                                        <img
                                            src={passportSrc}
                                            alt="Passport"
                                            className="img-thumbnail"
                                            style={{ width: "150px", height: "180px", objectFit: "cover" }}
                                            onError={(e) => { e.target.src = FALLBACK_IMG; }}
                                        />
                                        <div className="mt-2">
                                            <span className={`badge ${selectedRecord.Status === "Completed" ? "bg-success" : "bg-warning"}`}>
                                                {selectedRecord.Status}
                                            </span>
                                        </div>
                                        <a
                                            href={passportSrc}
                                            download={`${selectedRecord.StudentID || selectedRecord.AppID}-passport.jpg`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-sm btn-outline-primary mt-2 w-100"
                                        >
                                            Download Photo
                                        </a>
                                    </div>

                                    {/* Student Info */}
                                    <div className="col-md-9">
                                        <div className="row">
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Full Name</label>
                                                <p className="fw-bold mb-1">{selectedRecord.StudentName || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Student ID</label>
                                                <p className="fw-bold mb-1">{selectedRecord.StudentID || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Application ID</label>
                                                <p className="fw-bold mb-1">{selectedRecord.AppID || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Programme</label>
                                                <p className="fw-bold mb-1">{selectedRecord.Programme || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Gender</label>
                                                <p className="fw-bold mb-1">{selectedRecord.GENDER || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Blood Group</label>
                                                <p className="fw-bold mb-1">{selectedRecord.BLOOD_GROUP || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>NIN</label>
                                                <p className="fw-bold mb-1">{selectedRecord.NIN || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Date of Birth</label>
                                                <p className="fw-bold mb-1">
                                                    {selectedRecord.DOB ? formatDateAndTime(selectedRecord.DOB, "date") : "N/A"}
                                                </p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Email</label>
                                                <p className="fw-bold mb-1">{selectedRecord.EMAIL || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Phone</label>
                                                <p className="fw-bold mb-1">{selectedRecord.TELEPHONE || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Admission Year</label>
                                                <p className="fw-bold mb-1">{selectedRecord.ADMISSION_YEAR || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Admission Session</label>
                                                <p className="fw-bold mb-1">{selectedRecord.ADMISSION_SESSION || "N/A"}</p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Date Submitted</label>
                                                <p className="fw-bold mb-1">
                                                    {selectedRecord.InsertedDate ? formatDateAndTime(selectedRecord.InsertedDate, "date") : "N/A"}
                                                </p>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label text-muted mb-0" style={{ fontSize: "0.75rem" }}>Submitted By</label>
                                                <p className="fw-bold mb-1">{selectedRecord.InsertedBy || "N/A"}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                Close
                            </button>
                            {selectedRecord && selectedRecord.Status !== "Completed" && (
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    disabled={IsActionLoading}
                                    onClick={() => {
                                        markCompleted(selectedRecord.AppID);
                                        document.querySelector('[data-bs-dismiss="modal"]').click();
                                    }}
                                >
                                    Mark as Completed
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state) => {
    return {
        loginData: state.loginDetails,
    };
};

export default connect(mapStateToProps, null)(StudentIdCardReport);
